// Feature: docs/features/simplify-case-wizard.md
import React, { useState, useContext, useEffect } from 'react';
import {
  Box, Button, VStack, Heading, Text, useToast, HStack, 
  Card, CardHeader, CardBody, Flex, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, Badge
} from "@chakra-ui/react";
import { AttachmentIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { useDropzone } from 'react-dropzone';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import { useNavigate } from 'react-router-dom';

function CaseWizard() {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();

  // State for the new case being built
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // State for recent manual uploads
  const [recentCases, setRecentCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);

  // Fetch recent manual uploads on load
  useEffect(() => {
    fetchRecentManualUploads();
  }, [accessToken]);

  const fetchRecentManualUploads = async () => {
    if (!accessToken) return;
    setIsLoadingCases(true);
    try {
      const response = await fetch(`${window.env.REACT_APP_REVEDY_API_HOST}/api/search/cases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          query: '', 
          limit: 10,
          integration_source: 'manual upload'
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        setRecentCases(data.cases || []);
      }
    } catch (error) {
      console.error("Failed to fetch cases", error);
    } finally {
      setIsLoadingCases(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    
    setIsUploading(true);
    try {
      let caseIdToUse = currentCaseId;
      
      // Create case if it doesn't exist yet
      if (!caseIdToUse) {
        const randomCaseId = 'mn-' + Math.random().toString(36).substr(2, 9);
        const createRes = await callApi('/create_external_case', accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            external_case_id: randomCaseId,
            integration_source: { source: 'manual upload' }
          }),
        });
        const createData = await createRes.json();
        if (!createRes.ok) throw new Error(createData.error);
        caseIdToUse = createData.case_id;
        setCurrentCaseId(caseIdToUse);
      }
      
      // Upload all files concurrently
      const uploadPromises = acceptedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file_type_id', '5');
        formData.append('file', file);

        const uploadRes = await callApi(`/upload_file/${caseIdToUse}`, accessToken, {
          method: 'POST',
          body: formData,
        });
        
        if (!uploadRes.ok) throw new Error(`Upload failed for ${file.name}`);
        return { name: file.name, size: file.size };
      });

      const successfulUploads = await Promise.all(uploadPromises);
      
      // Add to local state for UI
      setUploadedFiles(prev => [...prev, ...successfulUploads]);
      toast({ title: `${successfulUploads.length} file(s) uploaded`, status: 'success', duration: 2000 });
      
      // Refresh the list of manual uploads
      fetchRecentManualUploads();
      
    } catch (error) {
      toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'text/plain': ['.txt']
    }
  });

  return (
    <Box fontSize="xl">
      <HeaderMenus />
      <Box p={6}>
        <VStack spacing={10} align="stretch">
          
          {/* Top Section: The Wizard */}
          <Box maxW="800px" mx="auto" w="full">
            <Card variant="outline" borderColor="blue.200" shadow="md">
            <CardHeader bg="blue.50" pb={4}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="md" color="blue.800">
                    Create New Case
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Upload medical records to create a new case.
                  </Text>
                </Box>
              </Flex>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                
                {/* Step 1: Upload */}
                <Box>
                  <Text fontWeight="bold" mb={2}>Upload Documents</Text>
                  <Box
                    {...getRootProps()}
                    w="full"
                    minH="120px"
                    p={6}
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderColor={isDragActive ? "blue.400" : "gray.300"}
                    bg={isDragActive ? "blue.50" : "gray.50"}
                    borderRadius="md"
                    cursor={isUploading ? "not-allowed" : "pointer"}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.2s"
                    _hover={!isUploading ? { borderColor: "blue.400", bg: "blue.50" } : {}}
                  >
                    <input {...getInputProps()} />
                    {isUploading ? (
                      <VStack spacing={2}>
                        <Spinner color="blue.500" />
                        <Text color="blue.500" fontWeight="medium">Uploading files...</Text>
                      </VStack>
                    ) : (
                      <VStack spacing={2}>
                        <AttachmentIcon boxSize={6} color={isDragActive ? "blue.500" : "gray.400"} />
                        <Text color={isDragActive ? "blue.600" : "gray.600"} fontWeight="medium" textAlign="center">
                          {isDragActive
                            ? "Drop the files here..."
                            : "Drag & drop files here, or click to select files"}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Supports PDF, PNG, JPG, TXT
                        </Text>
                      </VStack>
                    )}
                  </Box>
                  
                  {uploadedFiles.length > 0 && (
                    <Box mt={4} p={4} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                      <HStack mb={3}>
                        <CheckCircleIcon color="green.500" />
                        <Text fontSize="md" fontWeight="bold" color="green.800">
                          Case Created Successfully!
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="green.700" mb={4}>
                        You can add more files above, or click below to view the case.
                      </Text>
                      
                      <Box mb={4} p={3} bg="white" borderRadius="md" borderWidth="1px">
                        <Text fontSize="sm" fontWeight="bold" mb={2}>Attached Files ({uploadedFiles.length}):</Text>
                        <VStack align="stretch" spacing={1}>
                          {uploadedFiles.map((f, i) => (
                            <Text key={i} fontSize="sm" color="gray.700">• {f.name}</Text>
                          ))}
                        </VStack>
                      </Box>

                      <HStack spacing={4} mt={4}>
                        <Button
                          colorScheme="blue"
                          size="md"
                          flex="1"
                          onClick={() => navigate(`/ionm-strict-analysis/${currentCaseId}`)}
                        >
                          View Case
                        </Button>
                        <Button
                          variant="outline"
                          colorScheme="gray"
                          size="md"
                          flex="1"
                          onClick={() => {
                            setCurrentCaseId(null);
                            setUploadedFiles([]);
                          }}
                        >
                          Create Another Case
                        </Button>
                      </HStack>
                    </Box>
                  )}
                </Box>

              </VStack>
            </CardBody>
          </Card>
          </Box>

          {/* Bottom Section: Recent Manual Uploads */}
          <Card variant="outline" w="full">
            <CardHeader pb={2}>
              <Heading size="md">Recent Manual Uploads</Heading>
            </CardHeader>
            <CardBody pt={0}>
              {isLoadingCases ? (
                <Flex justify="center" p={8}><Spinner size="xl" /></Flex>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size="md">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Case ID</Th>
                        <Th>Date Created</Th>
                        <Th>Status</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recentCases.map(c => (
                        <Tr key={c.id} _hover={{ bg: 'gray.50' }}>
                          <Td fontWeight="bold">{c.external_case_id}</Td>
                          <Td>{new Date(c.date_created).toLocaleDateString()}</Td>
                          <Td>
                            <Badge 
                              colorScheme={
                                c.has_mn_analysis ? 'green' : 
                                c.job_status === 'pending' ? 'purple' : 
                                c.job_status === 'processing' ? 'orange' : 
                                c.job_status === 'failed' ? 'red' :
                                (c.cpt_status === 'completed' && c.icd10_status === 'completed') ? 'green' : 'gray'
                              }
                            >
                              {c.has_mn_analysis ? 'Completed' : 
                               c.job_status === 'pending' ? 'Queued' : 
                               c.job_status === 'processing' ? 'Processing' : 
                               c.job_status === 'failed' ? 'Failed' :
                               (c.cpt_status === 'completed' && c.icd10_status === 'completed') ? 'Completed' : 'Draft'}
                            </Badge>
                          </Td>
                          <Td>
                            <Button 
                              size="sm" 
                              colorScheme="blue" 
                              variant="outline"
                              onClick={() => navigate(`/ionm-strict-analysis/${c.external_case_id}`)}
                            >
                              View Case
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                      {recentCases.length === 0 && (
                        <Tr>
                          <Td colSpan={4} textAlign="center" py={4} color="gray.500">
                            No recent manual uploads found.
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>

        </VStack>
      </Box>
    </Box>
  );
}

export default CaseWizard;
